import { Typography } from '@tempmee/react';
import type { MouseEvent, ReactNode } from 'react';
import AnimatedCloseCircle from '~/components/shared/AnimatedCloseCircle';
import ConfirmPopup from '~/components/shared/ConfirmPopup';

interface ICancelShiftConfirmPopupProps {
  /** Swaps the copy + button label between shift and onsite-interview wording. */
  isInterview: boolean;
  /** Human date shown in the confirmation copy (e.g. "Thu, Jul 9, 2026"). */
  date?: ReactNode;
  /** Disables both buttons while the cancel mutation is in flight. */
  isPending: boolean;
  /** Dismiss without cancelling (Go Back / backdrop / X). */
  onClose: () => void;
  /** Confirm the cancellation. */
  onConfirm: () => void;
}

/**
 * The "Are you sure you want to cancel?" dialog shared by every shift-card wrapper
 * (Shift Overview, Schedule List, …). Presentational-only: the wrapper owns the open
 * state and the cancel mutation and passes `onConfirm`/`onClose`.
 */
export const CancelShiftConfirmPopup = ({
  isInterview,
  date,
  isPending,
  onClose,
  onConfirm,
}: ICancelShiftConfirmPopupProps) => (
  <ConfirmPopup
    Icon={() => <AnimatedCloseCircle />}
    title="Are you sure you want to cancel?"
    // Breathing room below the animated close-circle icon (0px gap without it).
    titleStyle={{ marginTop: 20 }}
    description={
      <>
        <Typography variant="body1" textAlign="center">
          {`Are you sure you would like to cancel the ${
            isInterview ? 'onsite interview' : 'shift'
          } for`}
        </Typography>
        <Typography variant="body1" bold textAlign="center">
          {date}
        </Typography>
      </>
    }
    closePopup={(event?: MouseEvent) => {
      event?.stopPropagation();
      onClose();
    }}
    leftButtonText="Go Back"
    leftButtonAction={onClose}
    rightButtonText={isInterview ? 'Cancel Interview' : 'Cancel Shift'}
    leftButtonDisabled={isPending}
    rightButtonDisabled={isPending}
    rightButtonAction={onConfirm}
    leftButtonStyle={isInterview ? { width: 180 } : {}}
    rightButtonStyle={isInterview ? { width: 180 } : {}}
  />
);
